import React from 'react';
import { Box, Button, Container, Paper, Typography, useTheme } from '@mui/material';
import {
  ArrowForward,
  FavoriteBorder,
  VisibilityOutlined,
  CampaignOutlined,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { isVisible } from '../../corelogic/model/section';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import {
  getCardStyles,
  getPrimaryButtonStyles,
  getSecondaryButtonStyles,
  getSectionStyles,
  heroSpacing,
  textMeasure,
  displayMeasure,
} from '../../shared/styles';
import { SEO } from './SEO';
import { BlockRenderer } from './blocks/BlockRenderer';
import { PageSections } from './PageSections';
import { Section, SectionHeading, CardGrid } from './PageSection';
import { InstagramFeed } from './InstagramFeed';

const contentUseCase = new ContentUseCase();

/** The three goals from the CI, in the order the identity document uses. */
const GOAL_ICONS = [FavoriteBorder, VisibilityOutlined, CampaignOutlined];

/**
 * Where the hero's second button leads when the CMS field is empty: the
 * library, which is what it has always pointed at.
 *
 * That page is built at /admin rather than in routes.ts, so its address is its
 * file name - renaming src/content/pages/ressourcen.json renames the page, and
 * the button then has to be pointed at the new name under „Kopfbereich".
 * Pointed at a page that does not exist, or one an editor has switched off,
 * the link falls through to the catch-all and lands on the start page, the
 * same as every other link to a page that is off.
 */
const RESOURCES_PATH = '/ressourcen';

/** A leading slash is what `useLocalizedPath` expects; an editor may omit it. */
const toPath = (value: string | undefined): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return RESOURCES_PATH;
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

export const HomePage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const lp = useLocalizedPath();
  const content = contentUseCase.getHomeContent(language);

  const addedBefore = (slot: string) =>
    contentUseCase.getExtraBlocksBefore('home', slot, language);

  // The hero is a section like any other and can be switched off at /admin.
  // Whether it is on decides which heading is the page's h1, so the sections
  // below have to know about it.
  const showHero = isVisible(content.hero);

  // The two teasers share one section, so it needs to know about both.
  const showVision = isVisible(content.visionTeaser);
  const showOrganisation = isVisible(content.organisationTeaser);

  const sections: Record<string, React.ReactNode> = {
    /* Introduction */
    intro: isVisible(content.intro) && (
      <Section labelledBy="home-intro">
        {/*
          The section's title, set in type like every other heading on the
          site. It opened with the wordmark drawn instead for a while - the
          collective's own name as artwork - and the header carries that same
          wordmark a few centimetres above: two of it above the fold, saying
          the same thing twice, and the second one not selectable, not
          searchable and not translated.

          With the hero switched off this heading is the page's h1, and with
          the hero on the h1 is the hero's.
        */}
        <Typography
          id="home-intro"
          variant="h2"
          component={showHero ? 'h2' : 'h1'}
          sx={{ ...displayMeasure, mx: 'auto', mb: 3, textAlign: 'center' }}
        >
          {content.intro.title}
        </Typography>
        {/* Set centred, like the heading above them and like the rest of the
            start page: this opening block is the page's own axis and the text
            stands on it rather than beside it. */}
        {content.intro.paragraphs.map((paragraph, i) => (
          <Typography
            key={i}
            variant="body1"
            sx={{ mb: 2.5, color: 'text.secondary', ...textMeasure, mx: 'auto', textAlign: 'center' }}
          >
            {paragraph}
          </Typography>
        ))}
      </Section>
    ),

    /* Three goals */
    goals: isVisible(content.goals) && (
      <Section labelledBy="home-goals">
        <SectionHeading
          id="home-goals"
          title={content.goals.title}
          description={content.goals.description}
          align="center"
        />
        <CardGrid min={260}>
          {content.goals.items.map((goal, i) => {
            const Icon = GOAL_ICONS[i] ?? FavoriteBorder;
            return (
              <Paper key={goal.key} elevation={0} sx={{ ...getCardStyles(theme), '&:hover': {} }}>
                {/* The symbol on its own, without the filled disc it used to
                    sit in: the disc was a third background inside a card
                    inside a section, and the site now carries one. */}
                <Icon aria-hidden sx={{ fontSize: 32, color: 'primary.dark', mb: 2 }} />
                <Typography variant="h4" component="h3" sx={{ mb: 1.5 }}>
                  {goal.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {goal.description}
                </Typography>
              </Paper>
            );
          })}
        </CardGrid>
        <Box sx={{ mt: 5, textAlign: 'center' }}>
          <Button
            component={Link}
            to={lp('/organisation')}
            sx={getSecondaryButtonStyles(theme)}
            endIcon={<ArrowForward />}
          >
            {content.goals.linkLabel}
          </Button>
        </Box>
      </Section>
    ),

    /* Vision and organisation teasers, side by side unless one is hidden */
    teasers: (showVision || showOrganisation) && (
      <Section labelledBy={showVision ? 'home-vision' : 'home-organisation'}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: showVision && showOrganisation ? '1fr 1fr' : '1fr',
            },
            gap: { xs: 5, md: 6 },
            textAlign: 'center',
          }}
        >
          {showVision && (
            <Box>
              <Typography id="home-vision" variant="h3" component="h2" sx={{ mb: 2 }}>
                {content.visionTeaser.title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                {content.visionTeaser.description}
              </Typography>
              <Button
                component={Link}
                to={lp('/organisation')}
                sx={getSecondaryButtonStyles(theme)}
                endIcon={<ArrowForward />}
              >
                {content.visionTeaser.linkLabel}
              </Button>
            </Box>
          )}
          {showOrganisation && (
            <Box>
              <Typography id="home-organisation" variant="h3" component="h2" sx={{ mb: 2 }}>
                {content.organisationTeaser.title}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                {content.organisationTeaser.description}
              </Typography>
              <Button
                component={Link}
                to={lp('/organisation')}
                sx={getSecondaryButtonStyles(theme)}
                endIcon={<ArrowForward />}
              >
                {content.organisationTeaser.linkLabel}
              </Button>
            </Box>
          )}
        </Box>
      </Section>
    ),

    /* Join us */
    joinTeaser: isVisible(content.joinTeaser) && (
      <Section labelledBy="home-join">
        <Box sx={{ textAlign: 'center' }}>
          <Typography id="home-join" variant="h2" component="h2" sx={{ mb: 2 }}>
            {content.joinTeaser.title}
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, color: 'text.secondary', ...textMeasure, mx: 'auto' }}
          >
            {content.joinTeaser.description}
          </Typography>
          {/* The filled button rather than the CI-blue band it used to sit on:
              the emphasis is now carried by the one element that is meant to
              be clicked, not by the section's colour. */}
          <Button
            component={Link}
            to={lp('/mitmachen')}
            sx={getPrimaryButtonStyles(theme)}
            endIcon={<ArrowForward />}
          >
            {content.joinTeaser.linkLabel}
          </Button>
        </Box>
      </Section>
    ),

    /* Latest Instagram posts */
    socialTeaser: isVisible(content.socialTeaser) && (
      <Section labelledBy="home-social">
        <SectionHeading
          id="home-social"
          title={content.socialTeaser.title}
          description={content.socialTeaser.description}
        />
        <InstagramFeed limit={3} />
        <Box sx={{ mt: 4 }}>
          <Button
            component={Link}
            to={lp('/instagram')}
            sx={getSecondaryButtonStyles(theme)}
            endIcon={<ArrowForward />}
          >
            {content.socialTeaser.linkLabel}
          </Button>
        </Box>
      </Section>
    ),

    /* Podcast */
    podcastTeaser: isVisible(content.podcastTeaser) && (
      <Section labelledBy="home-podcast">
        <Box sx={{ textAlign: 'center' }}>
          <Typography id="home-podcast" variant="h3" component="h2" sx={{ mb: 2 }}>
            {content.podcastTeaser.title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3.5, ...textMeasure, mx: 'auto' }}>
            {content.podcastTeaser.description}
          </Typography>
          <Button
            component={Link}
            to={lp('/podcast')}
            sx={getSecondaryButtonStyles(theme)}
            endIcon={<ArrowForward />}
          >
            {content.podcastTeaser.linkLabel}
          </Button>
        </Box>
      </Section>
    ),
  };

  return (
    <Box>
      <SEO page="home" />

      <BlockRenderer blocks={addedBefore('hero')} />

      {/*
        Hero. Switched off at /admin the start page opens on its first section
        instead, and the copy stays in the content file to switch back on.

        No logo of its own: the header carries the wordmark a few centimetres
        above, and the second one only pushed the headline down without saying
        anything the eyebrow and the headline do not. It opens on the headline
        itself, on the same width and the same centre line as the sections below
        it, so the start page has one axis rather than two.
      */}
      {showHero && (
        <Box component="section" sx={{ ...getSectionStyles(theme), ...heroSpacing }}>
          <Container maxWidth="xl" sx={{ textAlign: 'center' }}>
            {/* Optional, like every other eyebrow on the site: cleared at /admin
                it has to leave, not stand there as an empty line of space. */}
            {content.hero.eyebrow && (
              <Typography variant="overline" component="p" sx={{ color: 'primary.dark', mb: 1 }}>
                {content.hero.eyebrow}
              </Typography>
            )}
            <Typography
              variant="h1"
              component="h1"
              sx={{
                // Below the theme's h1: at 3.5rem this headline ran to three
                // lines and took the whole screen on its own.
                fontSize: 'clamp(2.125rem, 4.6vw, 3.25rem)',
                ...displayMeasure,
                mx: 'auto',
                mb: 2,
              }}
            >
              {content.hero.headline}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', mb: 3.5, ...textMeasure, mx: 'auto' }}
            >
              {content.hero.description}
            </Typography>
            {/* The two ways into the site a first-time reader wants: who we
                are, and what we have collected. "Mach mit" used to stand here
                as the filled button - it is the ask, and it now waits for the
                block further down the page that is about nothing else. */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                component={Link}
                to={lp('/organisation')}
                sx={getPrimaryButtonStyles(theme)}
                endIcon={<ArrowForward />}
              >
                {content.hero.primaryCta}
              </Button>
              <Button
                component={Link}
                to={lp(toPath(content.hero.secondaryCtaLink))}
                sx={getSecondaryButtonStyles(theme)}
              >
                {content.hero.secondaryCta}
              </Button>
            </Box>
          </Container>
        </Box>
      )}

      <PageSections
        page="home"
        sections={sections}
        order={content.sectionOrder}
        addedBefore={addedBefore}
      />
    </Box>
  );
};
